import { motion } from 'framer-motion';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Activity, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useProgrammeOverview } from '../hooks/useProgramme';
import { CrecProgrammeCard } from '../components/programme/CrecProgrammeCard';

interface StatCard {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
}

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

/** "6 published, 1 archived", leaving out zero counts; undefined when all are zero. */
const countsSubtitle = (counts: Record<string, number>) =>
  Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([label, n]) => `${n} ${label}`)
    .join(', ') || undefined;

/**
 * Admin dashboard page with welcome message and overview stats
 */
export default function DashboardPage() {
  const { user } = useAdminAuth();
  const { data: dashboardStats, isLoading } = useDashboardStats();
  const programme = useProgrammeOverview();

  const stats: StatCard[] = [
    {
      title: 'Activities',
      value: dashboardStats?.activities.total ?? '-',
      icon: <Activity className="w-6 h-6" />,
      subtitle: dashboardStats && countsSubtitle({
        published: dashboardStats.activities.published,
        archived: dashboardStats.activities.archived,
      }),
    },
    {
      title: 'Expert Tips',
      value: dashboardStats?.expertTips.total ?? '-',
      icon: <Lightbulb className="w-6 h-6" />,
      subtitle: dashboardStats && countsSubtitle({
        published: dashboardStats.expertTips.published,
        unpublished: dashboardStats.expertTips.unpublished,
        archived: dashboardStats.expertTips.archived,
      }),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-pine-display font-display text-pine-ink mb-2">
          {getGreeting()}, {user?.name || 'Admin'}!
        </h1>
        <p className="text-body text-pine-ink-soft">
          Welcome to the Play Moments admin dashboard. Here&apos;s an overview of your platform.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
              </CardHeader>
              <CardContent>
                <p className="text-caption text-pine-ink-soft mb-1">
                  {stat.title}
                </p>
                {isLoading ? (
                  <div className="h-8 w-16 bg-pine-line animate-pulse rounded" />
                ) : (
                  <p className="text-heading-3 text-pine-ink font-semibold">
                    {stat.value}
                  </p>
                )}
                {stat.subtitle && !isLoading && (
                  <p className="text-caption text-pine-ink-soft mt-1">
                    {stat.subtitle}
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* CREC programme */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <CrecProgrammeCard
          overview={programme.data?.programme}
          isLoading={programme.isLoading}
          isError={programme.isError}
        />
      </motion.div>
    </div>
  );
}
